import { Button } from 'finara-components';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import OtpInput from 'react-otp-input';

import { mfaService } from '@/services/mfa.service';

interface MfaVerificationModalProps {
  isOpen: boolean;
  factorId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export const MfaVerificationModal: React.FC<MfaVerificationModalProps> = ({
  isOpen,
  factorId,
  onSuccess,
  onCancel,
}) => {
  const [otpCode, setOtpCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [challengeId, setChallengeId] = useState<string | null>(null);

  // Create challenge when modal opens
  React.useEffect(() => {
    if (isOpen && factorId && !challengeId) {
      mfaService
        .createChallenge(factorId)
        .then(challenge => {
          setChallengeId(challenge.id);
        })
        .catch(error => {
          console.error('Error al crear el desafío MFA:', error);
          toast.error('Error al iniciar la verificación');
        });
    }
  }, [isOpen, factorId, challengeId]);

  const handleVerify = async () => {
    if (!challengeId || otpCode.length !== 6) {
      toast.error('Por favor ingresa un código de 6 dígitos');
      return;
    }

    setIsVerifying(true);
    try {
      await mfaService.verifyChallenge(factorId, challengeId, otpCode);
      onSuccess();
    } catch (_error) {
      toast.error('Código incorrecto. Intenta nuevamente.');
      setOtpCode('');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCancel = () => {
    setOtpCode('');
    setChallengeId(null);
    onCancel();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <button
        className="absolute inset-0 bg-black opacity-60 cursor-default"
        onClick={handleCancel}
        type="button"
        aria-label="Cerrar modal"
      />

      {/* Modal */}
      <div
        role="presentation"
        className="relative bg-surface border border-black/10 dark:border-white/15 rounded-lg shadow-2xl shadow-black/60 max-w-md w-full"
        onClick={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
      >
        <div className="p-6">
          <h2 className="text-xl font-bold text-brand-white mb-4">Verificación de Dos Factores</h2>

          <div className="space-y-4 mb-6">
            <p className="text-muted">
              Ingresa el código de 6 dígitos de tu aplicación de autenticación:
            </p>

            {/* OTP Input */}
            <div className="flex justify-center">
              <OtpInput
                value={otpCode}
                onChange={setOtpCode}
                numInputs={6}
                renderSeparator={<span className="mx-2 text-muted">-</span>}
                renderInput={(props: React.InputHTMLAttributes<HTMLInputElement>) => (
                  <input
                    {...props}
                    className="!w-12 h-14 text-center text-xl font-semibold border-2 border-border bg-surface text-brand-white rounded-lg focus:border-brand-blue focus:outline-none"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                  />
                )}
                shouldAutoFocus
              />
            </div>
          </div>

          <div className="flex gap-3">
            <Button
              variant="black"
              onClick={() => void handleVerify()}
              disabled={isVerifying || otpCode.length !== 6 || !challengeId}
              className="flex-1"
            >
              {isVerifying ? 'Verificando...' : 'Verificar'}
            </Button>
            <Button variant="white" onClick={handleCancel} className="flex-1">
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
